import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Modal } from '../components/Common';
import { AI_MODELS, buildScanPayload } from '../components/agentScanConfig';

const STATUS_COLORS = {
  initializing: '#6366f1', recon: '#3b82f6', scanning: '#f59e0b',
  paused: '#f97316', completed: '#22c55e', failed: '#ef4444', cancelled: '#6b7280',
};
const SEV_COLORS = { Critical: '#dc2626', High: '#ea580c', Medium: '#f59e0b', Low: '#eab308', Info: '#3b82f6' };
const APPROVAL_ICONS = {
  auth_credentials: '🔑', scope_expansion: '🌐', destructive_test: '⚠',
  exploit_confirm: '💥', manual_action: '🖐', budget_increase: '⏱',
};
const APPROVAL_COLORS = {
  pending: '#f59e0b', approved: '#22c55e', rejected: '#ef4444', expired: '#6b7280',
};

/**
 * Engagements worth pointing the agent at: the same set the Engagements page
 * calls "All Active" — everything that is neither Complete nor Cancelled.
 * Scanning a finished engagement produces findings against a report that has
 * already gone out.
 *
 * The currently selected engagement is shown regardless of this, because a
 * session auto-selects its own engagement when one is running. Filtering that
 * out would leave the dropdown blank while a scan was visibly in progress; it
 * is labelled with its status instead.
 */
const isScannable = (e) => e && e.status !== 'Complete' && e.status !== 'Cancelled';

export default function AgentScanner() {
  const { apiFetch, user, hasGroup } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const presetEngId = searchParams.get('engagementId') || '';

  const [engagements, setEngagements] = useState([]);
  const [selectedEng, setSelectedEng] = useState(presetEngId);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [reasoningLog, setReasoningLog] = useState([]);
  const [approvals, setApprovals] = useState([]);

  // Config
  const [mode, setMode] = useState('guided');
  const [aiModel, setAiModel] = useState('kimi-k2');
  const [budgetRequests, setBudgetRequests] = useState(50000);
  const [budgetTime, setBudgetTime] = useState(60);
  // 0 means no token ceiling, which is what every scan ran with before the
  // limit was enforced.
  const [budgetTokens, setBudgetTokens] = useState(0);
  // Unlimited requests: the scan is then bounded by time alone, which is the
  // limit an operator can actually reason about — nobody knows what 50,000
  // requests means, everybody knows what four hours means.
  const [unlimitedRequests, setUnlimitedRequests] = useState(false);
  // Accounts recorded on the engagement. The agent authenticates with these
  // and uses them as the columns of the authorization matrix, so more than one
  // is what makes vertical privilege escalation testable at all.
  const [engCredentials, setEngCredentials] = useState([]);
  const [budgetHours, setBudgetHours] = useState(4);
  // Off by default. Scans have been creating accounts on the target as a
  // matter of course, including one administrator.
  const [allowDestructive, setAllowDestructive] = useState(false);
  // A session the tester established themselves, applied before the first
  // request. The route for a login the agent cannot complete — MFA, SSO, a
  // captcha — and supplied here rather than at the mid-scan prompt so recon
  // maps the authenticated surface rather than a login page.
  const [requestsPerMinute, setRequestsPerMinute] = useState(0);
  const [probeSensitiveFiles, setProbeSensitiveFiles] = useState(true);
  const [browserFingerprint, setBrowserFingerprint] = useState(false);
  const [runNuclei, setRunNuclei] = useState(true);
  const [unauthenticatedOnly, setUnauthenticatedOnly] = useState(false);
  // The whole launch panel. Collapsed on arrival: the header carries the
  // engagement, mode, model and budget, and a Start Scan button, so the common
  // case — look at a scan, or start one with the settings already chosen —
  // needs no expansion at all. Opening it is one click.
  const [launchOpen, setLaunchOpen] = useState(false);
  // Collapsed by default. Open, this is the largest thing on the page, and
  // most scans never change any of it.
  const [setupOpen, setSetupOpen] = useState(false);
  const [cookieHelpOpen, setCookieHelpOpen] = useState(false);
  const [preAuthKind, setPreAuthKind] = useState('cookie');
  const [preAuthSecret, setPreAuthSecret] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [scopeConfirm, setScopeConfirm] = useState(null);
  // Which scope items the operator is actually authorising this run against.
  // An engagement normally lists several environments and a tester usually
  // wants one of them; before this, confirming the scope meant confirming all
  // of it, so a UAT test also hit production.
  const [scopeSelection, setScopeSelection] = useState([]);
  const [starting, setStarting] = useState(false);
  const [centerTab, setCenterTab] = useState('findings');
  const [pushing, setPushing] = useState(false);
  const [expandedChain, setExpandedChain] = useState(null);
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [pushResult, setPushResult] = useState(null);
  // Tracks an in-flight push so loadSessionDetail does not overwrite the
  // "Queued…" banner with a stale result from the previous attempt.
  const pushingRef = useRef(false);
  const [expandedTest, setExpandedTest] = useState(null);
  // Which Review categories are open. A scan produces 140+ anomalies arriving
  // interleaved by step, so they start collapsed and the operator opens the
  // class they want to triage.
  const [openAnomGroups, setOpenAnomGroups] = useState(() => new Set());
  const toggleAnomGroup = (type) => setOpenAnomGroups(prev => {
    const next = new Set(prev);
    if (next.has(type)) next.delete(type); else next.add(type);
    return next;
  });
  // Confirmed findings, grouped the same way. These start OPEN — unlike the
  // review queue these are results, and hiding a confirmed finding behind a
  // click is the wrong default.
  const [closedFindingGroups, setClosedFindingGroups] = useState(() => new Set());
  const toggleFindingGroup = (type) => setClosedFindingGroups(prev => {
    const next = new Set(prev);
    if (next.has(type)) next.delete(type); else next.add(type);
    return next;
  });

  // Resizable split
  const [leftWidth, setLeftWidth] = useState(320);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    const onMove = (ev) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (ev.clientX || ev.touches?.[0]?.clientX) - rect.left;
      setLeftWidth(Math.max(180, Math.min(x, rect.width - 420)));
    };
    const onUp = () => { dragging.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onUp);
  }, []);

  // Approval response
  const [respondingTo, setRespondingTo] = useState(null);
  const [approvalResponse, setApprovalResponse] = useState({});

  const selectStyle = {
    padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
  };

  const loadCredentials = useCallback(async (engId) => {
    if (!engId) { setEngCredentials([]); return; }
    try {
      const res = await apiFetch(`/agent-scanner/credentials/${engId}`);
      setEngCredentials(res.ok ? await res.json() : []);
    } catch { setEngCredentials([]); }
  }, [apiFetch]);

  useEffect(() => { loadCredentials(selectedEng); }, [selectedEng, loadCredentials]);

  const loadEngagements = useCallback(async () => {
    try {
      const res = await apiFetch('/engagements');
      if (res.ok) setEngagements(await res.json());
    } catch {}
  }, [apiFetch]);

  const loadAllSessions = useCallback(async () => {
    try {
      const res = await apiFetch('/agent-scanner/sessions');
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
        // Auto-select the first active/paused session
        if (!activeSession) {
          const active = data.find(s => ['initializing', 'recon', 'scanning', 'paused'].includes(s.status));
          if (active) {
            setSelectedEng(active.engagement_id);
            loadSessionDetail(active.id);
          }
        }
      }
    } catch {}
  }, [apiFetch]);

  const loadSessions = useCallback(async () => {
    await loadAllSessions();
  }, [loadAllSessions]);

  const loadSessionDetail = async (sessionId) => {
    try {
      const sessRes = await apiFetch(`/agent-scanner/session/${sessionId}`);
      if (sessRes.ok) {
        const data = await sessRes.json();
        // Ensure JSONB fields are objects, not strings
        if (typeof data.world_model === 'string') try { data.world_model = JSON.parse(data.world_model); } catch { data.world_model = {}; }
        if (typeof data.coverage_state === 'string') try { data.coverage_state = JSON.parse(data.coverage_state); } catch { data.coverage_state = {}; }
        if (typeof data.attack_chains === 'string') try { data.attack_chains = JSON.parse(data.attack_chains); } catch { data.attack_chains = []; }
        setActiveSession(data);
        setReasoningLog(data.reasoning_log || []);
        // Surface a push outcome recorded on the session. Without this, a push
        // that finished after the UI stopped waiting was never shown — not on
        // the open page and not on a reload — leaving the operator believing it
        // had failed when the findings were filed.
        if (data.push_result && !pushingRef.current) {
          try {
            const r = typeof data.push_result === 'string'
              ? JSON.parse(data.push_result) : data.push_result;
            setPushResult(describePush(r));
          } catch { /* a result we cannot read is not worth a banner */ }
        }
      }
    } catch {}
    try {
      const appRes = await apiFetch(`/agent-scanner/session/${sessionId}/approvals`);
      if (appRes.ok) setApprovals(await appRes.json());
    } catch {}
  };

  useEffect(() => { loadEngagements(); loadAllSessions(); }, [loadEngagements, loadAllSessions]);

  useEffect(() => {
    if (!activeSession || !['initializing', 'recon', 'scanning', 'paused'].includes(activeSession.status)) return;
    const interval = setInterval(() => loadSessionDetail(activeSession.id), 3000);
    return () => clearInterval(interval);
  }, [activeSession?.id, activeSession?.status]);

  // Ticks the Run time column for scans that are still going. The 3s poll
  // above only refreshes the ACTIVE session's detail, so without this a
  // running scan's elapsed time would sit frozen at whatever it was when the
  // list was last fetched. Costs no requests, and only runs while something
  // is actually live.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!sessions.some(isSessionLive)) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [sessions]);

  // Scope is confirmed before anything is probed. An engagement's scope is a
  // copy of the application's taken at creation and never re-synced, so it can
  // silently authorise a host that was removed from the application later.
  const openScopeConfirm = async () => {
    if (!selectedEng) return;
    setScopeConfirm({ loading: true, data: null });
    try {
      const res = await apiFetch(`/agent-scanner/scope-preview/${selectedEng}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setScopeConfirm(null);
        alert(err.error || 'Could not load the engagement scope');
        return;
      }
      const data = await res.json();
      setScopeConfirm({ loading: false, data });
      // Ticked by default: the engagement scope is what the scan is authorised
      // against, and the operator narrows from there.
      setScopeSelection((data.scopeItems || []).map(s => s.id));
    } catch {
      setScopeConfirm(null);
      alert('Could not load the engagement scope');
    }
  };

  const startScan = async () => {
    if (!selectedEng) return;
    setScopeConfirm(null);
    setStarting(true);
    try {
      const res = await apiFetch('/agent-scanner/start', {
        method: 'POST',
        // Shared with the "Send to AI Pentest Agent" launcher, so the two
        // surfaces cannot drift on what a budget means.
        body: JSON.stringify(buildScanPayload(selectedEng, {
          mode, aiModel, budgetRequests, budgetTime, budgetHours, budgetTokens,
          unlimitedRequests, allowDestructive, scopeItemIds: scopeSelection,
          preAuthKind, preAuthSecret, requestsPerMinute,
          probeSensitiveFiles, browserFingerprint, runNuclei, unauthenticatedOnly,
        })),
      });
      if (res.ok) {
        const data = await res.json();
        loadSessions();
        loadSessionDetail(data.id);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to start scan');
      }
    } catch {} finally { setStarting(false); }
  };

  const controlSession = async (sessionId, action) => {
    await apiFetch(`/agent-scanner/session/${sessionId}/${action}`, { method: 'POST' });
    loadSessionDetail(sessionId);
  };

  // File this scan's findings against the engagement. Repeated instances of one
  // issue are merged server-side, so the number filed is normally lower than
  // the number found — the result says so rather than leaving it to be guessed.
  // mode: '' (refuse a re-push), 'force' (file everything again), or 'new'
  // (file only what the engagement does not already hold).
  // Turn a finished push into the sentence the operator reads. Shared by the
  // queued-then-polled path below and by a result that is already on the
  // session when the page loads.
  const describePush = (body) => {
    if (body.error) return { ok: false, message: body.error };
    if (body.skipped) {
      return {
        ok: false, canForce: true, canPushNew: true,
        message: `Already pushed ${body.previously_pushed} finding(s) to this engagement. ` +
                 'Push new only files the ones it does not already have; ' +
                 'pushing everything again would file duplicates.',
      };
    }
    const bits = [`Filed ${body.pushed} finding${body.pushed === 1 ? '' : 's'}`];
    if (body.skipped_existing > 0) bits.push(`${body.skipped_existing} already on the engagement, skipped`);
    if (body.merged > 0) bits.push(`${body.merged} duplicate instances merged`);
    if (body.failed > 0) bits.push(`${body.failed} failed`);
    // Nothing new is a success, not a failure — it means the engagement
    // is already up to date with this scan.
    return {
      ok: body.failed === 0,
      message: (body.pushed === 0 && body.skipped_existing > 0)
        ? `Nothing new — all ${body.skipped_existing} finding(s) are already on the engagement`
        : bits.join(' — '),
    };
  };

  // The push is a request written to the session, which the scanner picks up on
  // its poll — the same way starting, pausing and stopping a scan work. The
  // backend used to call the scanner over HTTP, which has no route to it
  // outside docker compose, so the button answered "Server error" everywhere
  // else. The cost is that the answer arrives a few seconds later instead of
  // inline, so this waits for it rather than leaving the operator guessing.
  const pushFindings = async (sessionId, mode = '') => {
    setPushing(true);
    pushingRef.current = true;
    setPushResult(null);
    try {
      const res = await apiFetch(`/agent-scanner/session/${sessionId}/push`, {
        method: 'POST',
        body: JSON.stringify({ force: mode === 'force', skipExisting: mode === 'new' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPushResult({ ok: false, message: body.error || 'Push failed' });
        setPushing(false);
        loadSessionDetail(sessionId);
        return;
      }

      setPushResult({ ok: true, pending: true, message: 'Queued — filing findings…' });

      // Poll the session for the outcome. Bounded: a request that never
      // produces a result means no scanner is running, and saying so beats
      // spinning for ever.
      const deadline = Date.now() + 60000;
      for (;;) {
        await new Promise(r => setTimeout(r, 2000));
        let row = null;
        try {
          const r2 = await apiFetch(`/agent-scanner/session/${sessionId}`);
          // The detail route answers with the session row itself, flattened,
          // plus reasoning_log — not wrapped in a `session` key.
          if (r2.ok) row = await r2.json();
        } catch { /* keep waiting */ }
        const result = row && row.push_result;
        if (result) {
          setPushResult(describePush(typeof result === 'string' ? JSON.parse(result) : result));
          break;
        }
        if (Date.now() > deadline) {
          setPushResult({
            ok: false,
            message: 'Still running after a minute — a large scan takes longer. ' +
                     'The result is recorded on this scan; reopen it to see the ' +
                     'outcome. Nothing needs re-scanning.',
          });
          break;
        }
      }
    } catch {
      setPushResult({ ok: false, message: 'Push failed — could not reach the server' });
    }
    setPushing(false);
    pushingRef.current = false;
    loadSessionDetail(sessionId);
  };

  // Super Admins only. The backend refuses to delete a scan whose agent may
  // still be live, so a running scan has to be stopped first.
  const deleteSession = async (session, e) => {
    e.stopPropagation();
    const label = session.engagement_name || session.engagement_type || 'this scan';
    // What the scan will file, not how many times a probe fired. Three missing
    // headers across ten endpoints each is thirty instances and three findings;
    // this column showed thirty beside a Findings panel showing three, and a
    // tester had to work out which number to believe. The grouped count is the
    // one that reaches the engagement and the report. Older sessions have no
    // grouped count recorded, so they fall back to the raw one.
    const rawFindings = session.findings_count || 0;
    const grouped = session.findings_grouped_count;
    const findings = (grouped === null || grouped === undefined) ? rawFindings : grouped;
    const findingsTitle = (grouped === null || grouped === undefined)
      ? `${rawFindings} finding instance(s)`
      : `${grouped} finding${grouped === 1 ? '' : 's'} from ${rawFindings} instance(s) — ` +
        `repeated instances of one issue are filed as a single finding`;
    if (!window.confirm(
      `Delete the ${session.status} scan for "${label}"?\n\n` +
      (findings ? `It recorded ${findings} finding${findings === 1 ? '' : 's'}. ` : '') +
      'The scan record and its reasoning log will be removed. Findings already pushed to the ' +
      'engagement are not affected, and knowledge the agent learned is kept.\n\nThis cannot be undone.'
    )) return;

    setDeletingId(session.id);
    try {
      const res = await apiFetch(`/agent-scanner/session/${session.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to delete scan');
        return;
      }
      if (activeSession?.id === session.id) {
        setActiveSession(null);
        setReasoningLog([]);
        setApprovals([]);
      }
      loadSessions();
    } catch {
      alert('Failed to delete scan');
    } finally {
      setDeletingId(null);
    }
  };

  const respondToApproval = async (approvalId, action) => {
    // Number inputs hand back strings, and a cleared field gives ''. Coerce the
    // budget fields so the agent never receives a non-numeric top-up.
    const responseData = { ...approvalResponse };
    for (const key of ['additional_requests', 'additional_minutes']) {
      if (key in responseData) {
        const n = parseInt(responseData[key], 10);
        responseData[key] = Number.isFinite(n) && n > 0 ? n : 0;
      }
    }
    await apiFetch(`/agent-scanner/approvals/${approvalId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ action, responseData }),
    });
    setRespondingTo(null);
    setApprovalResponse({});
    if (activeSession) loadSessionDetail(activeSession.id);
  };

  const canDeleteScans = hasGroup('Super Admins');

  // Mirrors the backend's delete guard: an agent process may still be alive
  // for an active status, or for a 'paused' session with no interrupted_at
  // (parked in the approval wait). A checkpointed 'paused' row has no process.
  const isSessionLive = (s) =>
    ['initializing', 'recon', 'scanning'].includes(s.status)
    || (s.status === 'paused' && !s.interrupted_at);

  // How long a scan has been running, in milliseconds, or null when it never
  // started. started_at is set when the worker claims the row, which can be
  // several seconds after created_at, so this measures the scan rather than
  // the queue wait. A finished scan is frozen at completed_at; a live one
  // counts up against `now`.
  const runTimeMs = (s) => {
    const begun = s.started_at ? new Date(s.started_at).getTime() : null;
    if (!begun || Number.isNaN(begun)) return null;
    const ended = s.completed_at ? new Date(s.completed_at).getTime() : null;
    return Math.max(0, (ended && !Number.isNaN(ended) ? ended : now) - begun);
  };

  // Compact and aligned: "48s", "15m 13s", "2h 04m". Minutes and seconds are
  // zero-padded only when a larger unit precedes them, so the column reads as
  // a duration rather than a clock time.
  const formatRunTime = (ms) => {
    if (ms == null) return '—';
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m) return `${m}m ${String(sec).padStart(2, '0')}s`;
    return `${sec}s`;
  };

  const pendingApprovals = approvals.filter(a => a.status === 'pending');
  const isRunning = activeSession && ['initializing', 'recon', 'scanning'].includes(activeSession.status);

  // Fold the launch panel away once a scan is under way — that is the moment
  // the console below needs the screen and none of these controls can change
  // anything. Reopening is one click, and it stays open if the user reopens it.
  const foldedForRun = useRef(false);
  useEffect(() => {
    if (isRunning && !foldedForRun.current) {
      foldedForRun.current = true;
      setLaunchOpen(false);
    }
    if (!isRunning) foldedForRun.current = false;
  }, [isRunning]);
  const isPaused = activeSession?.status === 'paused';
  const isActive = activeSession && !['completed', 'cancelled', 'failed'].includes(activeSession.status);
  const needsAttention = isPaused && pendingApprovals.length > 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>AI Pentest Agent</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Autonomous penetration testing with human-in-the-loop controls
          </p>
        </div>
        {needsAttention && (
          <div style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🖐</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>{pendingApprovals.length} action{pendingApprovals.length > 1 ? 's' : ''} need{pendingApprovals.length === 1 ? 's' : ''} your approval</span>
          </div>
        )}
      </div>

      {/* Config Bar. Collapsible as a whole: six selects, three checkboxes,
          the scanner setup and the accounts panel add up to most of the screen,
          and none of it changes once a scan is running. */}
      {(() => {
        const eng = engagements.find(e => e.id === selectedEng);
        const engText = eng
          ? `${eng.client_name || ''} \u2014 ${eng.name || eng.engagement_type || ''}`.trim()
          : 'No engagement selected';
        const modelLabel = (AI_MODELS.find(m => m.value === aiModel) || {}).label || aiModel;
        const budgetText = unlimitedRequests
          ? `${budgetHours || 0}h, unlimited requests`
          : `${budgetRequests} requests, ${budgetTime || 0} min`;
        const summary = [
          engText,
          mode === 'guided' ? 'guided' : 'autonomous',
          modelLabel,
          budgetText,
          allowDestructive ? 'destructive' : null,
          unauthenticatedOnly ? 'unauthenticated only' : null,
        ].filter(Boolean).join(' \u00b7 ');
        return (
      <Card style={{ padding: 0, marginBottom: 16 }}>
        <div onClick={() => setLaunchOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                   cursor: 'pointer', userSelect: 'none',
                   borderBottom: launchOpen ? '1px solid var(--border)' : 'none' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{launchOpen ? '\u25be' : '\u25b8'}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
            Launch a scan
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {summary}
          </span>
          {!launchOpen && (
            <button onClick={e => { e.stopPropagation(); startScan(); }}
              disabled={!selectedEng || starting}
              style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: (!selectedEng || starting) ? 'not-allowed' : 'pointer',
                       fontSize: 12, fontFamily: 'inherit', fontWeight: 600, color: '#fff',
                       background: (!selectedEng || starting) ? 'var(--text-muted)' : 'var(--primary)' }}>
              {starting ? 'Starting\u2026' : 'Start Scan'}
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--primary)' }}>{launchOpen ? 'Hide' : 'Change'}</span>
        </div>
        {launchOpen && (
        <div style={{ padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Engagement</label>
            <select value={selectedEng} onChange={e => { setSelectedEng(e.target.value); setActiveSession(null); }} style={{ ...selectStyle, width: '100%' }}>
              <option value="">Select engagement...</option>
              {engagements
                .filter(e => isScannable(e) || e.id === selectedEng)
                .map(e => (
                  <option key={e.id} value={e.id}>
                    {e.client_name} — {e.name || e.engagement_type}
                    {isScannable(e) ? '' : ` (${e.status})`}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Mode</label>
            <select value={mode} onChange={e => setMode(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
              <option value="guided">Guided (Recommended)</option>
              <option value="autonomous">Autonomous</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>AI Model</label>
            <select value={aiModel} onChange={e => setAiModel(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
              {AI_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {unlimitedRequests ? 'Requests' : 'Requests'}
            </label>
            <input type="number" min="1" value={unlimitedRequests ? '' : budgetRequests}
              disabled={unlimitedRequests}
              placeholder={unlimitedRequests ? 'unlimited' : ''}
              title={unlimitedRequests
                ? 'No request ceiling — the time limit stops the scan'
                : 'Stop after this many HTTP requests'}
              onChange={e => setBudgetRequests(e.target.value)}
              style={{ ...selectStyle, width: '100%', opacity: unlimitedRequests ? 0.5 : 1 }} />
          </div>
          <div>
            {/* budgetTime was posted to the API with no control behind it, so a
                scan stopped at 60 minutes whatever the request budget said. */}
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {unlimitedRequests ? 'Hours' : 'Minutes'}
            </label>
            <input type="number" min={unlimitedRequests ? '0.5' : '1'}
              step={unlimitedRequests ? '0.5' : '5'}
              value={unlimitedRequests ? budgetHours : budgetTime}
              title={unlimitedRequests
                ? 'Stop after this many hours'
                : 'Stop after this many minutes'}
              onChange={e => unlimitedRequests
                ? setBudgetHours(e.target.value)
                : setBudgetTime(e.target.value)}
              style={{ ...selectStyle, width: '100%' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Tokens</label>
            <input type="number" min="0" step="100000" value={budgetTokens}
              title="Stop after this many model tokens. 0 means no token limit. This agent averages about 7,500 tokens per step."
              onChange={e => setBudgetTokens(e.target.value)} style={{ ...selectStyle, width: '100%' }} />
          </div>
          <Button onClick={openScopeConfirm} disabled={starting || !selectedEng || isRunning} style={{ whiteSpace: 'nowrap' }}>
            {starting ? 'Starting...' : '🚀 Start Scan'}
          </Button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={unlimitedRequests}
              onChange={e => setUnlimitedRequests(e.target.checked)} />
            Unlimited requests &mdash; stop on time instead
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: allowDestructive ? '#f59e0b' : 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={allowDestructive}
              onChange={e => setAllowDestructive(e.target.checked)} />
            Allow destructive tests
          </label>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {allowDestructive
              ? 'Registers up to 8 accounts (username starting attackpath-agent, at example.invalid) and does not delete them; writes to the record the scan signs in as and tries to put it back; bursts about 13 requests at each of 3 login, OTP or reset routes — these are deliberately failed attempts, so they rotate reset tokens and leave audit-log rows, though recipients are rewritten to a reserved address or a fictional 555 number so nothing reaches a real inbox. Formula-injection testing writes up to 7 rows that cannot be removed afterwards.'
              : 'The agent will not modify the target. Mass assignment, privilege escalation, rate limiting, password policy, formula injection and authenticated testing are skipped and reported as not tested.'}
          </span>
        </div>

        {/* A session the tester signed in for by hand. Sits beside Accounts
            because they answer the same question — who the scan runs as — but
            this one is for the logins an agent cannot pass on its own. */}
        {/* Scanner setup. One collapsed section rather than three open panels:
            these settings exist for targets that notice being scanned, which is
            the minority, and open they pushed everything else off the screen. */}
        {(() => {
          const rateLabel = Number(requestsPerMinute) > 0 ? `${requestsPerMinute}/min` : 'unlimited';
          const bits = [
            `rate ${rateLabel}`,
            probeSensitiveFiles ? 'file discovery on' : 'file discovery off',
            browserFingerprint ? 'as Chrome' : 'default fingerprint',
            runNuclei ? 'nuclei on' : 'nuclei off',
            unauthenticatedOnly ? 'unauthenticated only' : null,
            (preAuthSecret || '').trim() ? 'session supplied' : 'no session supplied',
          ];
          // The toggle contributes nothing to the summary when it is off.
          const parts = bits.filter(Boolean);
          return (
          <div style={{ marginTop: 10, borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            <div onClick={() => setSetupOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{setupOpen ? '\u25be' : '\u25b8'}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Scanner setup
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', flex: 1 }}>{parts.join(' \u00b7 ')}</span>
              <span style={{ fontSize: 11, color: 'var(--primary)' }}>{setupOpen ? 'Hide' : 'Change'}</span>
            </div>

            {setupOpen && (
            <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Rate */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 96 }}>Request rate</span>
                  <input type="number" min="0" step="10" value={requestsPerMinute}
                    onChange={e => setRequestsPerMinute(e.target.value)}
                    style={{ width: 80, padding: '4px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: 'var(--bg-card)', color: 'var(--text)', border: '1px solid var(--border)' }} />
                  {[0, 30, 60, 120].map(v => (
                    <button key={v} type="button" onClick={() => setRequestsPerMinute(v)}
                      style={{
                        padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                        background: Number(requestsPerMinute) === v ? 'var(--primary)' : 'var(--bg-card)',
                        color: Number(requestsPerMinute) === v ? '#fff' : 'var(--text-secondary)',
                        border: `1px solid ${Number(requestsPerMinute) === v ? 'var(--primary)' : 'var(--border)'}`,
                      }}>{v === 0 ? 'Unlimited' : `${v}/min`}</button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, paddingLeft: 104 }}>
                  {Number(requestsPerMinute) > 0
                    ? `About one request every ${(60 / Number(requestsPerMinute)).toFixed(1)}s, jittered, shared across all parallel probes. A slow scan needs a longer time budget to finish.`
                    : 'As fast as the target answers. Right for a lab; it is what gets a scan flagged by a bot manager.'}
                </div>
              </div>

              {/* Sensitive file discovery */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={probeSensitiveFiles}
                    onChange={e => setProbeSensitiveFiles(e.target.checked)} />
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>Look for configuration and backup files by name</span>
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, paddingLeft: 24 }}>
                  {probeSensitiveFiles
                    ? 'Requests .env, .git/config, terraform.tfstate, Gemfile, *.bak and 110 others. High yield, and the single most recognisable scanner signature there is — measured against a WAF-protected target, every block came from this sweep.'
                    : 'Skipped and reported as not tested. Route and API discovery still run; those drew no blocks on the same target.'}
                </div>
              </div>

              {/* Nuclei */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={runNuclei}
                    onChange={e => setRunNuclei(e.target.checked)} />
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>Run the Nuclei template sweep</span>
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, paddingLeft: 24 }}>
                  {runNuclei
                    ? 'Thousands of community templates. Across 25 measured scans it produced 2.6% of findings (43 of 1,636) while sending most of the requests — one scan sent 14,203 of them, found nothing, and was blocked part-way through.'
                    : 'Skipped and reported as not tested. The rest of the scan is unaffected, and it is the single biggest reduction in request volume available.'}
                </div>
              </div>

              {/* Fingerprint */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={browserFingerprint}
                    onChange={e => setBrowserFingerprint(e.target.checked)} />
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>Present as Google Chrome</span>
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, paddingLeft: 24 }}>
                  {browserFingerprint
                    ? 'Sends Chrome 131\u2019s user agent and full header set, in Chrome\u2019s order, instead of python-httpx/0.27.0 and four headers. It does not change the TLS handshake, which still fingerprints as Python \u2014 this removes the obvious tells, not the careful ones.'
                    : 'Every request announces python-httpx/0.27.0 with four headers. Nothing that ever shipped as a browser looks like that.'}
                </div>
              </div>

              {/* Unauthenticated only. Sits directly above the identity
                  controls because it turns them off. */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={unauthenticatedOnly}
                    onChange={e => setUnauthenticatedOnly(e.target.checked)} />
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>Test the unauthenticated surface only</span>
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, paddingLeft: 24 }}>
                  {unauthenticatedOnly
                    ? 'The scan never asks for credentials and never pauses for a person. It will not log in, will not register an account, and ignores any session below. Everything behind the login is reported as not tested, with that as the reason \u2014 it is not reported as clean.'
                    : 'A guided scan stops and waits up to 30 minutes when it decides it needs an identity. Right on an engagement with credentials; wrong on a public-surface sweep meant to run unattended.'}
                </div>
              </div>

              {/* Pre-authenticated session */}
              <div style={unauthenticatedOnly ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Pre-authenticated session</span>
                  {['cookie', 'token'].map(k => (
                    <button key={k} type="button" onClick={() => setPreAuthKind(k)}
                      style={{
                        padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                        background: preAuthKind === k ? 'var(--primary)' : 'var(--bg-card)',
                        color: preAuthKind === k ? '#fff' : 'var(--text-secondary)',
                        border: `1px solid ${preAuthKind === k ? 'var(--primary)' : 'var(--border)'}`,
                      }}>{k === 'cookie' ? 'Cookie header' : 'Bearer token'}</button>
                  ))}
                  <button type="button" onClick={() => setCookieHelpOpen(o => !o)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', fontSize: 11, fontFamily: 'inherit' }}>
                    {cookieHelpOpen ? 'Hide help' : 'Where do I get this?'}
                  </button>
                </div>
                <input value={preAuthSecret} onChange={e => setPreAuthSecret(e.target.value)}
                  placeholder={preAuthKind === 'token' ? 'Paste a token, with or without the Bearer prefix' : 'sid=abc; csrf=def; AWSALB=ghi'}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: 'var(--bg-card)', color: 'var(--text)', border: '1px solid var(--border)' }} />
                {(() => {
                  const raw = (preAuthSecret || '').trim();
                  if (!raw || preAuthKind === 'token') return null;
                  const names = raw.replace(/^cookie:\s*/i, '').split(';').map(x => x.trim())
                    .filter(x => x.includes('=')).map(x => x.split('=')[0].trim()).filter(Boolean);
                  const ok = names.length > 0;
                  return (
                    <div style={{
                      fontSize: 11, marginTop: 5, padding: '4px 8px', borderRadius: 6,
                      background: ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                      border: `1px solid ${ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                      color: ok ? '#16a34a' : '#dc2626',
                    }}>
                      {ok ? `${names.length} cookie${names.length === 1 ? '' : 's'} read: ${names.slice(0, 6).join(', ')}${names.length > 6 ? ', \u2026' : ''}`
                          : 'No name=value pairs found \u2014 this does not look like a Cookie header.'}
                    </div>
                  );
                })()}
                {cookieHelpOpen && preAuthKind === 'cookie' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.55, padding: '8px 10px', borderRadius: 6, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                    <strong style={{ color: 'var(--text)' }}>You do not pick a cookie \u2014 copy the whole header.</strong>
                    <ol style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      <li>Sign in to the target in your own browser, completing any OTP or SSO step.</li>
                      <li>Press <strong>F12</strong>, open <strong>Network</strong>, reload the page.</li>
                      <li>Click any request to the application \u2014 the top one usually does.</li>
                      <li>Under <strong>Request Headers</strong>, right-click <code>Cookie</code> &rarr; <em>Copy value</em>.</li>
                    </ol>
                    <div style={{ marginTop: 4 }}>
                      Not <em>Application &rarr; Cookies</em>: it lists them one per row, which is what makes
                      people think they must choose. They must not \u2014 the app usually needs the identity
                      cookie, an SSO cookie, a CSRF token and any bot-protection cookies together.
                    </div>
                    <div style={{ color: 'var(--warning, #f59e0b)', marginTop: 4 }}>
                      Not <code>document.cookie</code>: it cannot see HttpOnly cookies, and those are
                      usually the ones that authenticate you.
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
          );
        })()}

        {/* Which identities the scan will run as. Blank is a valid answer —
            it just means only the unauthenticated surface gets tested. */}
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Accounts ({engCredentials.length})
            </span>
            {selectedEng && (
              <a href={`/engagements/${selectedEng}`} onClick={e => { e.preventDefault(); navigate(`/engagements/${selectedEng}`); }}
                style={{ fontSize: 11, color: 'var(--primary)', textDecoration: 'none' }}>
                Manage on the engagement &rarr;
              </a>
            )}
          </div>
          {engCredentials.length > 0 ? (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {engCredentials.map(c => (
                  <span key={c.id} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                    {c.label}
                    <span style={{ color: 'var(--text-muted)', marginLeft: 5 }}>{c.credential_type}</span>
                  </span>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                The scan signs in as each of these and compares what they are shown,
                which is what makes privilege escalation between roles detectable.
                Secrets stay encrypted on the engagement and are never entered here.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              None recorded. The scan will test the unauthenticated surface only
              {allowDestructive
                ? ', apart from a throwaway account it registers itself.'
                : ' — with destructive tests off it cannot register one either, so authenticated surface goes untested.'}
            </div>
          )}
        </div>

        {mode === 'guided' && !unauthenticatedOnly && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>🛡</span> Guided mode pauses for your approval before: authentication,
            scope expansion, a budget top-up, and any captcha or multi-factor challenge
            that only a person can clear
          </div>
        )}
        {mode === 'guided' && unauthenticatedOnly && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>🛡</span> Guided mode still pauses for scope expansion and a budget
            top-up, but not for authentication \u2014 this scan does not authenticate
          </div>
        )}
        </div>
        )}
      </Card>
        );
      })()}

      {/* Approval Queue — always visible when there are pending items */}
      {pendingApprovals.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 16, border: '2px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.03)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#f59e0b', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
            🖐 Awaiting Your Approval
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingApprovals.map(a => (
              <div key={a.id} style={{ padding: 14, borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{APPROVAL_ICONS[a.request_type] || '❓'}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{a.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{a.request_type.replace(/_/g, ' ')}</div>
                    </div>
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{new Date(a.created_at).toLocaleTimeString()}</span>
                </div>
                {a.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.5 }}>{a.description}</p>}

                {/* Context details */}
                {a.context && Object.keys(a.context).length > 0 && (
                  <div style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: 10, fontSize: 12 }}>
                    {a.context.url && <div><strong>URL:</strong> <code style={{ fontSize: 11, color: 'var(--primary)' }}>{a.context.url}</code></div>}
                    {a.context.method && <div><strong>Method:</strong> {a.context.method}</div>}
                    {a.context.form_fields && <div><strong>Form fields:</strong> {a.context.form_fields.join(', ')}</div>}
                    {a.context.endpoints && <div><strong>New endpoints:</strong> {a.context.endpoints.length} discovered</div>}
                    {a.context.test_type && <div><strong>Test:</strong> {a.context.test_type}</div>}
                    {a.context.impact && <div><strong>Potential impact:</strong> {a.context.impact}</div>}
                    {a.request_type === 'budget_increase' && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
                        <div><strong>Requests used:</strong> {(a.context.requests_made || 0).toLocaleString()} / {(a.context.max_requests || 0).toLocaleString()}</div>
                        <div><strong>Time used:</strong> {a.context.time_elapsed_min}m / {a.context.max_time_min}m</div>
                        <div><strong>Coverage:</strong> {a.context.coverage_percent}%</div>
                        <div><strong>Still untested:</strong> {a.context.untested_combinations}</div>
                        <div><strong>Findings so far:</strong> {a.context.findings_so_far}</div>
                        <div><strong>Top-up #:</strong> {a.context.extension_number}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Budget top-up form */}
                {a.request_type === 'budget_increase' && respondingTo === a.id && (
                  <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      Allocate additional budget
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Input label="Additional requests" type="number" min="0"
                        value={approvalResponse.additional_requests ?? (a.context?.suggested_requests || 25000)}
                        onChange={e => setApprovalResponse(r => ({ ...r, additional_requests: e.target.value }))}
                        style={{ marginBottom: 0 }} />
                      <Input label="Additional minutes" type="number" min="0"
                        value={approvalResponse.additional_minutes ?? (a.context?.suggested_minutes || 60)}
                        onChange={e => setApprovalResponse(r => ({ ...r, additional_minutes: e.target.value }))}
                        style={{ marginBottom: 0 }} />
                    </div>
                    {/* The resulting total, not just the increment. A top-up was
                        approved as "+2,500,000" without anyone seeing that it meant
                        2,525,000 requests and ten hours. */}
                    {(() => {
                      const addR = Number(approvalResponse.additional_requests ?? (a.context?.suggested_requests || 0)) || 0;
                      const addM = Number(approvalResponse.additional_minutes ?? (a.context?.suggested_minutes || 0)) || 0;
                      const curR = Number(a.context?.current_max_requests || 0);
                      const curM = Number(a.context?.current_max_minutes || 0);
                      const capR = Number(a.context?.cap_requests || 0);
                      const capM = Number(a.context?.cap_minutes || 0);
                      const newR = curR + addR, newM = curM + addM;
                      const overR = capR > 0 && newR > capR;
                      const overM = capM > 0 && newM > capM;
                      return (
                        <div style={{ fontSize: 11, marginTop: 8, color: (overR || overM) ? '#f59e0b' : 'var(--text-secondary)' }}>
                          New total: <strong>{Math.min(newR, capR || newR).toLocaleString()}</strong> requests
                          {' · '}<strong>{(Math.min(newM, capM || newM) / 60).toFixed(1)}</strong> hours
                          {(overR || overM) && (
                            <span> &mdash; clamped: a scan cannot exceed 4&times; the budget it was launched with
                              ({capR.toLocaleString()} requests, {(capM / 60).toFixed(1)} hours)</span>
                          )}
                        </div>
                      );
                    })()}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                      Rejecting finishes the scan and writes up what it already found.
                    </div>
                  </div>
                )}

                {/* Auth credentials form */}
                {/* A challenge only a person can clear. Three ways to answer,
                    because a one-time code only covers MFA — for a captcha, an
                    SSO redirect or anything else a human clears in a browser,
                    the useful thing to hand back is the session that resulted. */}
                {a.request_type === 'manual_action' && respondingTo === a.id && (
                  <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      {a.context?.challenge === 'captcha' ? 'Solve the captcha, then hand back the session'
                                                          : 'Provide the second factor'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, wordBreak: 'break-all' }}>
                      {a.context?.url}
                      {(a.context?.detected_by || []).length > 0 && (
                        <span> &middot; detected: {a.context.detected_by.join(', ')}</span>
                      )}
                    </div>
                    {a.context?.challenge !== 'captcha' && (
                      <Input label="One-time code" value={approvalResponse.code || ''}
                        onChange={e => setApprovalResponse(r => ({ ...r, code: e.target.value }))}
                        placeholder="e.g. 123456" style={{ marginBottom: 8 }} />
                    )}
                    <Input label="Bearer token from a session you signed in yourself"
                      value={approvalResponse.token || ''}
                      onChange={e => setApprovalResponse(r => ({ ...r, token: e.target.value }))}
                      placeholder="Paste a token, with or without the Bearer prefix"
                      style={{ marginBottom: 8 }} />
                    <Input label="or Cookie header" value={approvalResponse.cookie || ''}
                      onChange={e => setApprovalResponse(r => ({ ...r, cookie: e.target.value }))}
                      placeholder="name=value; other=value"
                      style={{ marginBottom: 0 }} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                      Rejecting is a valid answer: the scan continues against the
                      unauthenticated surface, and the authenticated surface is
                      reported as not tested rather than as clean.
                    </div>
                  </div>
                )}

                {/* Two ways to give the agent an identity, chosen explicitly.
                    Credentials alone cannot pass a login that also wants a
                    one-time code or bounces through SSO, and on those the agent
                    would spend failed logins against a real account. Handing it
                    a session the operator established themselves is the only
                    route that works there, so it is offered up front rather
                    than only after a challenge is detected mid-login. */}
                {a.request_type === 'auth_credentials' && respondingTo === a.id && (() => {
                  const mode = approvalResponse.auth_mode || 'credentials';
                  const setMode = m => setApprovalResponse(r => ({ ...r, auth_mode: m }));
                  const tab = (on) => ({
                    flex: 1, padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                    fontSize: 12, fontWeight: on ? 600 : 500, fontFamily: 'inherit',
                    textAlign: 'center',
                    background: on ? 'var(--primary)' : 'var(--bg-card)',
                    color: on ? '#fff' : 'var(--text-secondary)',
                    border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                  });
                  return (
                  <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      How should the agent authenticate?
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button type="button" style={tab(mode === 'credentials')}
                        onClick={() => setMode('credentials')}>Sign in for me</button>
                      <button type="button" style={tab(mode === 'manual')}
                        onClick={() => setMode('manual')}>I&rsquo;ll authenticate myself</button>
                    </div>

                    {mode === 'credentials' ? (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <Input label="Username / Email" value={approvalResponse.username || ''}
                            onChange={e => setApprovalResponse(r => ({ ...r, username: e.target.value }))}
                            style={{ marginBottom: 0 }} />
                          <Input label="Password" type="password" value={approvalResponse.password || ''}
                            onChange={e => setApprovalResponse(r => ({ ...r, password: e.target.value }))}
                            style={{ marginBottom: 0 }} />
                        </div>
                        {a.context?.auth_type === 'bearer' && (
                          <Input label="Bearer Token" value={approvalResponse.token || ''}
                            onChange={e => setApprovalResponse(r => ({ ...r, token: e.target.value }))}
                            placeholder="Paste token here" style={{ marginBottom: 0, marginTop: 8 }} />
                        )}
                        {a.context?.auth_type === 'api_key' && (
                          <Input label="API Key" value={approvalResponse.api_key || ''}
                            onChange={e => setApprovalResponse(r => ({ ...r, api_key: e.target.value }))}
                            placeholder="Paste API key" style={{ marginBottom: 0, marginTop: 8 }} />
                        )}
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                          The agent submits these to the login form itself. If the login also
                          asks for a one-time code, or redirects to an identity provider, it
                          cannot complete that &mdash; choose &ldquo;I&rsquo;ll authenticate
                          myself&rdquo; instead rather than spending failed logins on a real
                          account.
                        </div>
                      </>
                    ) : (
                      <>
                        <Input label="Bearer token from a session you signed in yourself"
                          value={approvalResponse.token || ''}
                          onChange={e => setApprovalResponse(r => ({ ...r, token: e.target.value }))}
                          placeholder="Paste a token, with or without the Bearer prefix"
                          style={{ marginBottom: 8 }} />
                        <Input label="or Cookie header"
                          value={approvalResponse.cookie || ''}
                          onChange={e => setApprovalResponse(r => ({ ...r, cookie: e.target.value }))}
                          placeholder="name=value; other=value"
                          style={{ marginBottom: 0 }} />
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                          Sign in yourself in a browser, then paste what that session gave you
                          &mdash; the Authorization token, or the whole Cookie header from any
                          authenticated request. The agent uses it as-is and will not submit
                          the login form, so nothing is typed at a real account. This is the
                          route for MFA, SSO and captchas.
                        </div>
                      </>
                    )}
                  </div>
                  );
                })()}

                {/* Scope expansion — show discovered endpoints */}
                {a.request_type === 'scope_expansion' && a.context?.endpoints && respondingTo === a.id && (
                  <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: 10, maxHeight: 150, overflowY: 'auto' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Discovered endpoints to add to scope:</div>
                    {a.context.endpoints.map((ep, i) => (
                      <div key={i} style={{ fontSize: 12, fontFamily: 'monospace', padding: '2px 0', color: 'var(--text)' }}>{ep}</div>
                    ))}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {respondingTo !== a.id ? (
                    <>
                      {/* Types that need values must open their form first —
                          approving straight through would submit an empty
                          response (no credentials, or a zero budget top-up). */}
                      {a.request_type === 'auth_credentials' ? (
                        <Button onClick={() => setRespondingTo(a.id)} style={{ fontSize: 12 }}>Provide Credentials</Button>
                      ) : a.request_type === 'budget_increase' ? (
                        <Button style={{ fontSize: 12 }}
                          onClick={() => {
                            setApprovalResponse({
                              additional_requests: a.context?.suggested_requests || 25000,
                              additional_minutes: a.context?.suggested_minutes || 60,
                            });
                            setRespondingTo(a.id);
                          }}>
                          Allocate More Budget
                        </Button>
                      ) : (
                        <Button onClick={() => respondToApproval(a.id, 'approved')}
                          style={{ fontSize: 12, background: '#22c55e', borderColor: '#22c55e' }}>
                          Approve
                        </Button>
                      )}
                      <Button variant="danger" style={{ fontSize: 12 }} onClick={() => respondToApproval(a.id, 'rejected')}>
                        {a.request_type === 'auth_credentials' ? 'Skip (No Auth)'
                          : a.request_type === 'budget_increase' ? 'Finish Scan'
                          : 'Reject'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => respondToApproval(a.id, 'approved')} style={{ fontSize: 12, background: '#22c55e', borderColor: '#22c55e' }}>
                        Submit & Continue
                      </Button>
                      <Button variant="secondary" style={{ fontSize: 12 }} onClick={() => { setRespondingTo(null); setApprovalResponse({}); }}>
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Main 3-Column Layout */}
      {activeSession && (
        <div ref={containerRef} style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
          {/* Left: Agent Reasoning */}
          <Card style={{ padding: 12, maxHeight: 500, overflowY: 'auto', width: leftWidth, minWidth: 180, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Agent Log</span>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 8,
                background: (STATUS_COLORS[activeSession.status] || '#6b7280') + '18',
                color: STATUS_COLORS[activeSession.status] || '#6b7280',
                textTransform: 'uppercase',
              }}>{activeSession.status}</span>
            </div>
            {reasoningLog.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', padding: 8, textAlign: 'center' }}>
                {activeSession.status === 'initializing' ? 'Session created. Waiting for agent worker to pick up the job...' :
                 isRunning ? 'Agent is working...' : 'No activity yet.'}
              </div>
            ) : (
              reasoningLog.slice(0, 40).map((log, i) => {
                let reasoning = '';
                try {
                  const plan = typeof log.planned_action === 'string' ? JSON.parse(log.planned_action) : log.planned_action;
                  reasoning = plan?.reasoning || '';
                } catch {}
                return (
                  <div key={log.id || i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: 9, marginBottom: 2 }}>
                      <span>#{log.step_number} · {log.phase}</span>
                      <span>{log.model_used || 'kimi'}</span>
                    </div>
                    <div style={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                      {reasoning || log.observation?.substring(0, 120) || log.phase}
                    </div>
                  </div>
                );
              })
            )}
          </Card>

          {/* Drag Handle */}
          <div onMouseDown={onDragStart} onTouchStart={onDragStart}
            style={{ width: 8, cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, userSelect: 'none' }}>
            <div style={{ width: 3, height: 32, borderRadius: 2, background: 'var(--border)', transition: 'background 0.15s' }}
              onMouseEnter={e => e.target.style.background = 'var(--primary)'}
              onMouseLeave={e => e.target.style.background = 'var(--border)'} />
          </div>

          {/* Center: Tabbed Phase View */}
          <Card style={{ padding: 0, flex: 1, minWidth: 0, marginRight: 16, display: 'flex', flexDirection: 'column', maxHeight: 500 }}>
            {(() => {
              const endpoints = activeSession.world_model?.endpoints || [];
              // Collapsed the same way the push collapses them, so this tab and
              // the report agree. They did not: the scan stores one finding per
              // check that confirmed a vulnerability, and grouping happened only
              // at push time — so a single injectable `email` on /rest/user/login,
              // reached by the credential sweep, the sqli engine, the auth_bypass
              // engine and the context-injection sweep, showed as eight identical
              // rows here and one row in the report.
              //
              // Mirrors PentestAgent._group_key exactly: a finding that names a
              // parameter is identified by that parameter, because an injectable
              // input is one vulnerability however many checks reach it. Without
              // a parameter the substance is in the prose, so details is the key
              // — which keeps four "Exposed Internal URL" findings separate while
              // collapsing 27 identical "Missing HSTS header" rows into one.
              const findings = (() => {
                const raw = activeSession.world_model?.findings || [];
                const norm = (t) => (t || '')
                  .replace(/\s+\(\d+\s+endpoints?\)$/, '')
                  // URL or absolute path only. Stripping any trailing token
                  // after an em-dash erased the CVE id from every
                  // "Known vulnerability ... — CVE-xxxx" title.
                  .replace(/\s+[—-]\s+(?:https?:\/\/|\/)\S*$/, '')
                  .trim();
                // Mirrors PentestAgent.VULN_FAMILY: the SQL engine confirms a
                // login bypass by injecting into a parameter, so a result
                // labelled auth_bypass and one labelled sqli on the same
                // endpoint+parameter are one vulnerability. Narrow on purpose —
                // an XSS and a SQLi in the same input are two findings.
                const FAMILY = { auth_bypass: 'sqli' };
                const FALLBACK_TITLE = /^[A-Z][A-Z_]{2,}\s+[—-]\s+/;
                const keyOf = (f) => {
                  const fam = FAMILY[f.vuln_type] || f.vuln_type || '';
                  const param = (f.parameter || '').trim();
                  if (param) {
                    const ep = (f.endpoint || '').split('?')[0].replace(/\/+$/, '');
                    // Title and severity are not in this key: the same bug
                    // arrives under two names and two payloads depending on
                    // which queue reached it.
                    return JSON.stringify([fam, ep, param]);
                  }
                  return JSON.stringify([fam, norm(f.title), f.severity || 'Info',
                                         (f.details || '').trim()]);
                };
                const groups = new Map();
                raw.forEach(f => {
                  const k = keyOf(f);
                  if (!groups.has(k)) {
                    // First instance represents the group, as at push time.
                    groups.set(k, { ...f, _count: 0, _endpoints: [] });
                  }
                  const g = groups.get(k);
                  // A readable title beats the "SQLI - login" fallback, and a
                  // higher severity beats a lower one, so a merged group is
                  // never represented by the uglier or the milder of the two.
                  const SEV = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };
                  const better = (a, b) => {
                    const fa = FALLBACK_TITLE.test(a.title || ''), fb = FALLBACK_TITLE.test(b.title || '');
                    if (fa !== fb) return fb;
                    return (SEV[b.severity] ?? 9) < (SEV[a.severity] ?? 9);
                  };
                  if (better(g, f)) { Object.assign(g, f, { _count: g._count, _endpoints: g._endpoints }); }
                  g._count += 1;
                  if (f.endpoint && !g._endpoints.includes(f.endpoint)) g._endpoints.push(f.endpoint);
                  // Keep the richest evidence the group has rather than the
                  // first, so collapsing never loses a screenshot or a payload.
                  if (!g.evidence?.payload && f.evidence?.payload) g.evidence = f.evidence;
                  if (!(g.screenshots || []).length && (f.screenshots || []).length) {
                    g.screenshots = f.screenshots;
                  }
                });
                return [...groups.values()];
              })();
              const chains = activeSession.attack_chains || [];
              const techStack = activeSession.world_model?.technology_stack || {};
              const failedCount = activeSession.world_model?.failed_count || 0;

              // Build test attempts from reasoning log
              const testAttempts = reasoningLog.filter(l => l.phase === 'evaluate').map(l => {
                let plan = {};
                try { plan = typeof l.planned_action === 'string' ? JSON.parse(l.planned_action) : (l.planned_action || {}); } catch {}
                const results = plan.results || [];
                const hasVuln = results.some(r => r.reflected || (r.status === 500));
                return { step: l.step_number, target: plan.target || '', test_type: plan.test_type || '', results, hasVuln };
              }).filter(a => a.target);

              const coverageMatrix = activeSession.coverage_state?.matrix || {};
              const coverageEntries = Object.keys(coverageMatrix).length;
              const coveragePct = activeSession.coverage_state?.coverage_percent || 0;

              const anomalies = activeSession.world_model?.anomalies || [];
              const inputSurfaces = activeSession.world_model?.input_surfaces || [];
              const inputFieldCount = inputSurfaces.reduce((n, s) => n + (s.fields?.length || 0), 0);

              const PHASE_TABS = [
                { id: 'findings', label: `Findings (${findings.length})` },
                { id: 'recon', label: `Recon (${endpoints.length})` },
                { id: 'inputs', label: `Inputs (${inputFieldCount})` },
                { id: 'testing', label: `Testing (${testAttempts.length})` },
                { id: 'anomalies', label: `Review (${anomalies.length})` },
                { id: 'coverage', label: `Coverage (${coveragePct}%)` },
                ...(chains.length > 0 ? [{ id: 'chains', label: `Chains (${chains.length})` }] : []),
              ];

              return (
                <>
                  <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    {PHASE_TABS.map(tab => (
                      <button key={tab.id}
                        onClick={() => setCenterTab(tab.id)}
                        style={{
                          flex: 1, padding: '10px 8px', background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 12, fontWeight: centerTab === tab.id ? 700 : 500,
                          color: centerTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                          borderBottom: centerTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                          fontFamily: 'inherit', transition: 'all 0.15s',
                        }}>{tab.label}</button>
                    ))}
                  </div>
                  <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>

                    {/* Findings Tab */}
                    {centerTab === 'findings' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Confirmed Findings</h3>
                          {findings.length > 0 && (
                            <Button variant="primary" disabled={pushing || isRunning || isPaused}
                              title={isRunning || isPaused
                                ? 'Findings can be pushed once the scan finishes'
                                : 'File these findings against the engagement'}
                              onClick={() => pushFindings(activeSession.id)}
                              style={{ fontSize: 11, padding: '4px 12px' }}>
                              {pushing ? 'Pushing...' : 'Push to Engagement'}
                            </Button>
                          )}
                        </div>
                        {pushResult && (
                          <div style={{
                            marginBottom: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                            background: pushResult.ok ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
                            border: `1px solid ${pushResult.ok ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
                            color: pushResult.ok ? '#16a34a' : '#dc2626',
                          }}>
                            <span>{pushResult.message}</span>
                            <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              {pushResult.canPushNew && (
                                <Button variant="primary" disabled={pushing}
                                  title="File only the findings this engagement does not already have"
                                  style={{ fontSize: 10, padding: '3px 10px' }}
                                  onClick={() => pushFindings(activeSession.id, 'new')}>
                                  Push new only
                                </Button>
                              )}
                              {pushResult.canForce && (
                                <Button variant="secondary" disabled={pushing}
                                  title="File every finding again, including ones already on the engagement"
                                  style={{ fontSize: 10, padding: '3px 10px' }}
                                  onClick={() => pushFindings(activeSession.id, 'force')}>
                                  Push anyway
                                </Button>
                              )}
                            </span>
                          </div>
                        )}
                        {findings.length > 0 ? (() => {
                          // Same grouping as the Review tab, by check. A scan
                          // files 50+ findings and the header checks alone are
                          // dozens of near-identical rows, which buries the two
                          // or three that matter. Severity leads the sort,
                          // because for findings — unlike anomalies — the worst
                          // class belongs at the top regardless of how many
                          // rows it has. Original indices travel with each item
                          // so the per-row expansion keys stay stable.
                          const SEV_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };
                          const byType = new Map();
                          findings.forEach((f, i) => {
                            const key = (f.vuln_type || 'other').toUpperCase();
                            if (!byType.has(key)) byType.set(key, []);
                            byType.get(key).push({ f, i });
                          });
                          const groups = [...byType.entries()].map(([type, items]) => ({
                            type, items,
                            worst: Math.min(...items.map(x => SEV_RANK[x.f.severity] ?? 9)),
                            counts: items.reduce((acc, x) => {
                              acc[x.f.severity] = (acc[x.f.severity] || 0) + 1;
                              return acc;
                            }, {}),
                          })).sort((a, b) => a.worst - b.worst
                                          || b.items.length - a.items.length
                                          || a.type.localeCompare(b.type));

                          return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {groups.map(g => {
                            const isGroupOpen = !closedFindingGroups.has(g.type);
                            return (
                            <div key={g.type} style={{ borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
                              <div onClick={() => toggleFindingGroup(g.type)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                  cursor: 'pointer', background: 'var(--bg-input)',
                                  borderLeft: `3px solid ${SEV_COLORS[Object.keys(SEV_RANK)[g.worst]] || 'var(--border)'}`,
                                  borderBottom: isGroupOpen ? '1px solid var(--border)' : 'none' }}>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)',
                                  transform: isGroupOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▸</span>
                                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>{g.type}</span>
                                <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                  {Object.keys(SEV_RANK).filter(sev => g.counts[sev]).map(sev => (
                                    <span key={sev} style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                      background: (SEV_COLORS[sev] || '#6b7280') + '18', color: SEV_COLORS[sev] }}>
                                      {g.counts[sev]} {sev}
                                    </span>
                                  ))}
                                </span>
                                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)',
                                  fontVariantNumeric: 'tabular-nums' }}>
                                  {g.items.length} finding{g.items.length === 1 ? '' : 's'}
                                </span>
                              </div>

                              {isGroupOpen && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, background: 'var(--bg)' }}>
                            {g.items.map(({ f, i }) => {
                              const fkey = f.id || String(i);
                              const open = expandedFinding === fkey;
                              const steps = f.steps || [];
                              const ev = f.evidence || {};
                              const sigs = ev.signals || [];
                              return (
                              <div key={fkey}
                                style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: `1px solid ${open ? (SEV_COLORS[f.severity] || 'var(--border)') + '66' : 'var(--border)'}`, cursor: 'default' }}>
                                {/* Only the header toggles. The body is reproduction
                                    steps, payloads and raw request text a tester needs
                                    to copy into a report or a terminal; with the handler
                                    on the whole card, selecting any of it collapsed the
                                    finding on mouse-up and the selection was lost. */}
                                <div onClick={() => setExpandedFinding(open ? null : fkey)}
                                  style={{ cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontSize: 13, fontWeight: 600 }}>{f.title}</span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{open ? '\u25be' : '\u25b8'}</span>
                                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: (SEV_COLORS[f.severity] || '#6b7280') + '18', color: SEV_COLORS[f.severity] }}>{f.severity}</span>
                                  </span>
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, wordBreak: 'break-all' }}>{f.endpoint}</div>
                                {f.vuln_type && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 6 }}>{f.vuln_type.toUpperCase()}</span>}
                                {f.parameter && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 6 }}>PARAM: {f.parameter}</span>}
                                {f.confirmed && <span style={{ fontSize: 9, color: '#22c55e', fontWeight: 700 }}>CONFIRMED</span>}
                                {/* Say that a row stands for several, rather than
                                    silently dropping the duplicates. Independent
                                    confirmations are corroboration, not noise. */}
                                {f._count > 1 && (
                                  <span title={`Confirmed independently by ${f._count} checks`}
                                    style={{
                                      fontSize: 9, fontWeight: 700, marginLeft: 8, padding: '1px 6px',
                                      borderRadius: 4, background: 'rgba(34,197,94,0.12)', color: '#16a34a',
                                    }}>
                                    ×{f._count} CHECKS
                                  </span>
                                )}
                                {(f._endpoints || []).length > 1 && (
                                  <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 8 }}>
                                    {f._endpoints.length} endpoints
                                  </span>
                                )}
                                {!open && steps.length > 0 && (
                                  <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 8 }}>
                                    {steps.length} step{steps.length === 1 ? '' : 's'} to reproduce
                                  </span>
                                )}
                                </div>

                                {open && (
                                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', userSelect: 'text', cursor: 'auto' }}>
                                    {steps.length > 0 ? (
                                      <>
                                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
                                          Steps to reproduce
                                        </div>
                                        <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                          {steps.map((st, si) => (
                                            <li key={si} style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                              {/* Steps arrive in two shapes. The probe
                                                  engines write {description}; the discovery
                                                  sweep writes {action, expected}. Reading only
                                                  description rendered an empty bullet for every
                                                  step of those findings, which reads as "no
                                                  steps to reproduce" when the steps were there
                                                  all along. */}
                                              {typeof st === 'string'
                                                ? st
                                                : (st.description
                                                   || [st.action, st.expected && `→ ${st.expected}`]
                                                        .filter(Boolean).join(' ')
                                                   || JSON.stringify(st))}
                                            </li>
                                          ))}
                                        </ol>
                                      </>
                                    ) : (
                                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                        {f.details || 'No further detail recorded for this finding.'}
                                      </div>
                                    )}

                                    {sigs.length > 0 && (
                                      <div style={{ marginTop: 10 }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 5 }}>
                                          Why this is a finding
                                        </div>
                                        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                          {sigs.map((sg, gi) => (
                                            <li key={gi} style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{sg}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {(ev.payload || ev.request) && (
                                      <div style={{ marginTop: 10 }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 5 }}>
                                          Request
                                        </div>
                                        <div style={{ fontSize: 11, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 4, padding: '6px 9px', wordBreak: 'break-all', color: 'var(--text-secondary)' }}>
                                          {ev.request || ev.payload}
                                        </div>
                                      </div>
                                    )}

                                    {(ev.baseline || ev.response) && (
                                      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {ev.baseline && (
                                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                            Baseline: HTTP {ev.baseline.status} &middot; {ev.baseline.length} bytes
                                          </div>
                                        )}
                                        {ev.response && (
                                          <div style={{ fontSize: 10, color: SEV_COLORS[f.severity] || 'var(--text-muted)' }}>
                                            With payload: HTTP {ev.response.status}
                                            {ev.response.length != null ? ` \u00b7 ${ev.response.length} bytes` : ''}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {(f.screenshots || []).length > 0 && (
                                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}>
                                        {f.screenshots.length} evidence image{f.screenshots.length === 1 ? '' : 's'} attached &mdash; visible in the generated report
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                              </div>
                              )}
                            </div>
                            );
                          })}
                          </div>
                          );
                        })() : (
                          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                            {isRunning || isPaused ? 'Scanning for vulnerabilities...' : 'No findings.'}
                          </div>
                        )}
                        {/* Approval History */}
                        {approvals.filter(a => a.status !== 'pending').length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Approval History</h3>
                            {approvals.filter(a => a.status !== 'pending').map(a => (
                              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 11 }}>
                                <span>{APPROVAL_ICONS[a.request_type]}</span>
                                <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{a.title}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: APPROVAL_COLORS[a.status] }}>{a.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Recon Tab */}
                    {centerTab === 'recon' && (
                      <>
                        {/* Tech Stack Summary */}
                        {Object.keys(techStack).length > 0 && (
                          <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Technology Stack</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {Object.entries(techStack).map(([k, v]) => (
                                <span key={k} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                                  <span style={{ color: 'var(--text-muted)' }}>{k}:</span> <span style={{ fontWeight: 600 }}>{String(v)}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Directory Listing */}
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                          Discovered Endpoints ({endpoints.length})
                        </div>
                        {endpoints.length > 0 ? (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead>
                                <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
                                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-secondary)', width: 50 }}>Code</th>
                                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-secondary)' }}>Path</th>
                                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-secondary)', width: 40 }}>Auth</th>
                                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-secondary)', width: 100 }}>Content-Type</th>
                                </tr>
                              </thead>
                              <tbody>
                                {endpoints
                                  .sort((a, b) => {
                                    try { return new URL(a.url).pathname.localeCompare(new URL(b.url).pathname); } catch { return 0; }
                                  })
                                  .map((ep, i) => {
                                    let path;
                                    try { path = new URL(ep.url).pathname || '/'; } catch { path = ep.url; }
                                    const sc = ep.status || ep.response_code || 0;
                                    const statusColor = sc === 0 ? 'var(--text-muted)' : sc < 300 ? '#22c55e' : sc < 400 ? '#3b82f6' : sc < 500 ? '#f59e0b' : '#ef4444';
                                    return (
                                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '4px 10px', fontFamily: 'monospace', color: statusColor, fontWeight: 600 }}>{sc || '—'}</td>
                                        <td style={{ padding: '4px 10px', fontFamily: 'monospace', color: 'var(--text)', wordBreak: 'break-all' }} title={ep.url}>{path}</td>
                                        <td style={{ padding: '4px 10px', textAlign: 'center' }}>{ep.auth ? '🔒' : ''}</td>
                                        <td style={{ padding: '4px 10px', fontSize: 10, color: 'var(--text-muted)' }}>{(ep.tech || '').split(';')[0]}</td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                            {isRunning ? 'Discovering endpoints...' : 'No endpoints discovered.'}
                          </div>
                        )}
                      </>
                    )}

                    {/* Testing Tab */}
                    {centerTab === 'testing' && (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                          Test Attempts ({testAttempts.length}) {failedCount > 0 && <span style={{ fontWeight: 400 }}>· {failedCount} failed payloads</span>}
                        </div>
                        {testAttempts.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {testAttempts.map((a, i) => {
                              let path;
                              try { path = new URL(a.target).pathname || '/'; } catch { path = a.target; }
                              const isExpanded = expandedTest === i;
                              return (
                                <div key={i}>
                                  <div onClick={() => setExpandedTest(isExpanded ? null : i)} style={{
                                    padding: '8px 12px', borderRadius: isExpanded ? '6px 6px 0 0' : 6,
                                    background: a.hasVuln ? 'rgba(239,68,68,0.06)' : 'var(--bg-input)',
                                    border: `1px solid ${a.hasVuln ? 'rgba(239,68,68,0.25)' : 'var(--border)'}`,
                                    borderBottom: isExpanded ? 'none' : undefined,
                                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                  }}>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>▸</span>
                                    <span style={{
                                      width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 10, fontWeight: 700, flexShrink: 0,
                                      background: a.hasVuln ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.1)',
                                      color: a.hasVuln ? '#ef4444' : '#22c55e',
                                    }}>{a.hasVuln ? '!' : '✓'}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <span style={{
                                          fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                                          background: 'var(--bg)', border: '1px solid var(--border)',
                                          textTransform: 'uppercase', color: 'var(--text-secondary)',
                                        }}>{a.test_type}</span>
                                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.target}>{path}</span>
                                      </div>
                                      {a.results.length > 0 && (
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                                          {a.results.length} payload{a.results.length !== 1 ? 's' : ''} tested
                                          {a.results.some(r => r.reflected) && <span style={{ color: '#f59e0b', marginLeft: 4 }}>· reflected</span>}
                                          {a.results.some(r => r.error) && <span style={{ color: '#ef4444', marginLeft: 4 }}>· errors</span>}
                                        </div>
                                      )}
                                    </div>
                                    <span style={{ fontSize: 9, color: 'var(--text-muted)', flexShrink: 0 }}>#{a.step}</span>
                                  </div>
                                  {isExpanded && a.results.length > 0 && (
                                    <div style={{
                                      border: `1px solid ${a.hasVuln ? 'rgba(239,68,68,0.25)' : 'var(--border)'}`,
                                      borderTop: 'none', borderRadius: '0 0 6px 6px',
                                      background: 'var(--bg)', padding: 10, maxHeight: 300, overflowY: 'auto',
                                    }}>
                                      {a.results.map((r, ri) => (
                                        <div key={ri} style={{ marginBottom: ri < a.results.length - 1 ? 10 : 0 }}>
                                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                                            Payload {ri + 1}: <code style={{ color: '#f59e0b' }}>{r.payload}</code>
                                            <span style={{ marginLeft: 8, color: r.status < 400 ? '#22c55e' : r.status < 500 ? '#f59e0b' : '#ef4444' }}>{r.status}</span>
                                            {r.reflected && <span style={{ marginLeft: 6, color: '#ef4444', fontWeight: 700 }}>REFLECTED</span>}
                                          </div>
                                          {r.http && (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                              <div>
                                                <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' }}>Request</div>
                                                <pre style={{ fontSize: 10, fontFamily: 'monospace', background: 'var(--bg-card)', padding: 8, borderRadius: 4, border: '1px solid var(--border)', overflow: 'auto', maxHeight: 150, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text)' }}>
{r.http.request?.method} {r.http.request?.url}{r.http.request?.body ? '\n\n' + r.http.request.body : ''}
                                                </pre>
                                              </div>
                                              <div>
                                                <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' }}>Response ({r.http.response?.status})</div>
                                                <pre style={{ fontSize: 10, fontFamily: 'monospace', background: 'var(--bg-card)', padding: 8, borderRadius: 4, border: '1px solid var(--border)', overflow: 'auto', maxHeight: 150, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text)' }}>
{r.http.response?.body?.substring(0, 500) || '(empty)'}
                                                </pre>
                                              </div>
                                            </div>
                                          )}
                                          {!r.http && r.error && (
                                            <div style={{ fontSize: 10, color: '#ef4444', fontFamily: 'monospace', padding: 4 }}>{r.error}</div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                            {isRunning ? 'Testing in progress...' : 'No tests executed yet.'}
                          </div>
                        )}
                      </>
                    )}

                    {/* Review Tab — differed from baseline, not proven */}
                    {centerTab === 'anomalies' && (
                      anomalies.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                          {isRunning ? 'Watching for responses that differ from the baseline…'
                                     : 'Nothing flagged for manual review.'}
                        </div>
                      ) : (() => {
                        // Grouped by check, because a flat list of 140+ rows
                        // buries the shape of the queue: most of it is one
                        // class repeated across endpoints, and the few classes
                        // with only a handful of rows are usually the
                        // interesting ones. Original indices travel with each
                        // item so the per-row expansion keys stay unique and
                        // stable across groups.
                        const byType = new Map();
                        anomalies.forEach((a, i) => {
                          const key = a.test_type || 'other';
                          if (!byType.has(key)) byType.set(key, []);
                          byType.get(key).push({ a, i });
                        });
                        const groups = [...byType.entries()]
                          .map(([type, items]) => ({ type, items }))
                          .sort((x, y) => y.items.length - x.items.length
                                       || x.type.localeCompare(y.type));
                        // One category is not a grouping decision to make the
                        // operator click through.
                        const autoOpen = groups.length === 1 ? groups[0].type : null;

                        return (
                        <>
                          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 12,
                            background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', color: 'var(--text-secondary)' }}>
                            These responses <strong>behaved differently from the endpoint's clean baseline</strong> but
                            could not be proven exploitable. They are not findings — they are the cases worth a
                            tester's eyes.
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {groups.map(g => {
                            const isGroupOpen = openAnomGroups.has(g.type) || autoOpen === g.type;
                            const endpointCount = new Set(g.items.map(x => x.a.endpoint)).size;
                            return (
                            <div key={g.type} style={{ borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
                              <div onClick={() => toggleAnomGroup(g.type)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                  cursor: 'pointer', background: 'var(--bg-input)',
                                  borderBottom: isGroupOpen ? '1px solid var(--border)' : 'none' }}>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)',
                                  transform: isGroupOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▸</span>
                                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                  {g.type}
                                </span>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                  {g.items.length} anomal{g.items.length === 1 ? 'y' : 'ies'}
                                  {' · '}
                                  {endpointCount} endpoint{endpointCount === 1 ? '' : 's'}
                                </span>
                                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '2px 8px',
                                  borderRadius: 10, background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                                  border: '1px solid rgba(245,158,11,0.25)', fontVariantNumeric: 'tabular-nums' }}>
                                  {g.items.length}
                                </span>
                              </div>

                              {isGroupOpen && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8, background: 'var(--bg)' }}>
                            {g.items.map(({ a, i }) => {
                              let path = a.endpoint;
                              try { path = new URL(a.endpoint).pathname || '/'; } catch {}
                              const isOpen = expandedTest === `anom-${i}`;
                              return (
                                <div key={i} style={{ borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', overflow: 'hidden' }}>
                                  <div onClick={() => setExpandedTest(isOpen ? null : `anom-${i}`)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▸</span>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--bg)', border: '1px solid var(--border)', textTransform: 'uppercase' }}>
                                      {a.test_type}
                                    </span>
                                    <code style={{ fontSize: 12, fontWeight: 600 }} title={a.endpoint}>{path}</code>
                                    {a.parameter && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>param: <code>{a.parameter}</code></span>}
                                    <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>#{a.step}</span>
                                  </div>

                                  <div style={{ padding: '0 12px 10px 30px', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                    {(a.signals || []).map((sig, si) => (
                                      <span key={si} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10,
                                        background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>
                                        {sig}
                                      </span>
                                    ))}
                                  </div>

                                  {a.reasoning && (
                                    <div style={{ padding: '0 12px 10px 30px', fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                      {a.reasoning}
                                    </div>
                                  )}

                                  {isOpen && (
                                    <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)', padding: 10 }}>
                                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>
                                        Payload
                                      </div>
                                      <code style={{ fontSize: 11, color: '#f59e0b', wordBreak: 'break-all' }}>{a.payload || '(none)'}</code>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                                        <div>
                                          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' }}>Request</div>
                                          <pre style={{ fontSize: 10, fontFamily: 'monospace', background: 'var(--bg-card)', padding: 8, borderRadius: 4, border: '1px solid var(--border)', maxHeight: 150, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{a.http?.request ? `${a.http.request.method} ${a.http.request.url}` : '(not captured)'}
                                          </pre>
                                        </div>
                                        <div>
                                          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' }}>
                                            Response ({a.status}{a.baseline_status != null ? ` · baseline ${a.baseline_status}` : ''})
                                          </div>
                                          <pre style={{ fontSize: 10, fontFamily: 'monospace', background: 'var(--bg-card)', padding: 8, borderRadius: 4, border: '1px solid var(--border)', maxHeight: 150, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{a.response_snippet || '(empty)'}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                              </div>
                              )}
                            </div>
                            );
                          })}
                          </div>
                        </>
                        );
                      })()
                    )}

                    {/* Inputs Tab — where the app accepts user data */}
                    {centerTab === 'inputs' && (() => {
                      const ROLE_STYLE = {
                        user:     { bg: 'rgba(59,130,246,0.12)',  fg: '#3b82f6', label: 'user input' },
                        password: { bg: 'rgba(239,68,68,0.12)',   fg: '#ef4444', label: 'password' },
                        hidden:   { bg: 'rgba(107,114,128,0.15)', fg: 'var(--text-secondary)', label: 'hidden' },
                        file:     { bg: 'rgba(245,158,11,0.12)',  fg: '#f59e0b', label: 'file' },
                        token:    { bg: 'rgba(107,114,128,0.10)', fg: 'var(--text-muted)', label: 'token' },
                      };
                      const SOURCE_LABEL = {
                        dom: 'rendered page', html: 'page source',
                        url: 'observed URL', collection: 'API spec',
                      };
                      const testable = inputSurfaces.reduce(
                        (n, s) => n + (s.fields || []).filter(f => f.role !== 'token').length, 0);

                      if (inputSurfaces.length === 0) {
                        return (
                          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                            {isRunning ? 'Looking for forms, parameters and API fields…'
                                       : 'No user inputs discovered.'}
                          </div>
                        );
                      }

                      return (
                        <>
                          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                            {[
                              { label: 'Surfaces', value: inputSurfaces.length, color: 'var(--primary)' },
                              { label: 'Fields', value: inputFieldCount, color: 'var(--text)' },
                              { label: 'Testable', value: testable, color: '#22c55e' },
                              { label: 'Forms', value: inputSurfaces.filter(s => s.kind === 'form').length, color: '#3b82f6' },
                              { label: 'API', value: inputSurfaces.filter(s => s.kind === 'api').length, color: '#8b5cf6' },
                            ].map(s => (
                              <div key={s.label} style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', textAlign: 'center', minWidth: 76 }}>
                                <div style={{ fontSize: 17, fontWeight: 700, color: s.color }}>{s.value}</div>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                              </div>
                            ))}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {inputSurfaces.map((s, i) => {
                              let path = s.action;
                              try { path = new URL(s.action).pathname || '/'; } catch {}
                              return (
                                <div key={i} style={{ borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', overflow: 'hidden' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                                      {s.method || 'GET'}
                                    </span>
                                    <code style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 120, wordBreak: 'break-all' }} title={s.action}>{path}</code>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{s.kind}</span>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>· {SOURCE_LABEL[s.source] || s.source}</span>
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px' }}>
                                    {(s.fields || []).map((f, fi) => {
                                      const st = ROLE_STYLE[f.role] || ROLE_STYLE.user;
                                      return (
                                        <span key={fi} title={`${f.type}${f.required ? ' · required' : ''}${f.role === 'token' ? ' · app-generated, not a user input' : ''}`}
                                          style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            padding: '3px 9px', borderRadius: 12, fontSize: 11,
                                            background: st.bg, color: st.fg,
                                            border: '1px solid var(--border)',
                                            opacity: f.role === 'token' ? 0.6 : 1,
                                          }}>
                                          <code style={{ fontWeight: 600 }}>{f.name}</code>
                                          <span style={{ fontSize: 9, opacity: 0.85 }}>{f.type}</span>
                                          {f.required && <span style={{ fontSize: 9, color: '#ef4444' }}>*</span>}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)' }}>
                            Fields marked <em>token</em> are app-generated (CSRF and similar) and are not treated as
                            user inputs. Everything else — including file uploads and hidden fields — is fed to the
                            agent as a target for injection testing.
                          </div>
                        </>
                      );
                    })()}

                    {/* Coverage Tab */}
                    {centerTab === 'coverage' && (
                      <>
                        {/* Summary stats */}
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                          {[
                            { label: 'Endpoints', value: coverageEntries, color: 'var(--primary)' },
                            { label: 'Coverage', value: `${coveragePct}%`, color: coveragePct >= 80 ? '#22c55e' : coveragePct >= 40 ? '#f59e0b' : '#ef4444' },
                            { label: 'Untested', value: activeSession.coverage_state?.untested_count || 0, color: '#f59e0b' },
                            { label: 'Total Tests', value: activeSession.coverage_state?.total_combinations || 0, color: 'var(--text-secondary)' },
                          ].map(s => (
                            <div key={s.label} style={{ padding: '10px 16px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', textAlign: 'center', minWidth: 80 }}>
                              <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                            </div>
                          ))}
                        </div>
                        {/* Legend */}
                        <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 11 }}>
                          <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', verticalAlign: 'middle', marginRight: 4 }} /> Clean</span>
                          <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', verticalAlign: 'middle', marginRight: 4 }} /> Vulnerable</span>
                          <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: 'transparent', border: '1px solid var(--border)', verticalAlign: 'middle', marginRight: 4 }} /> Untested</span>
                        </div>
                        {/* Matrix */}
                        {coverageEntries > 0 ? (
                          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                              <thead>
                                <tr style={{ background: 'var(--bg-card)' }}>
                                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, background: 'var(--bg-card)', zIndex: 1 }}>Endpoint</th>
                                  {['sqli', 'xss', 'idor', 'auth', 'ssrf', 'lfi', 'rce', 'info', 'csrf', 'hdr'].map(t => (
                                    <th key={t} style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid var(--border)', textTransform: 'uppercase', color: 'var(--text-muted)', fontSize: 10 }}>{t}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(coverageMatrix).map(([ep, tests]) => {
                                  let path;
                                  try { path = new URL(ep).pathname || '/'; } catch { path = ep; }
                                  const testTypes = ['sqli', 'xss', 'idor', 'auth_bypass', 'ssrf', 'lfi', 'rce', 'info_disclosure', 'csrf', 'header_check'];
                                  return (
                                    <tr key={ep} style={{ borderBottom: '1px solid var(--border)' }}>
                                      <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontSize: 10, whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', left: 0, background: 'var(--bg)' }} title={ep}>{path}</td>
                                      {testTypes.map(tt => {
                                        const s = tests[tt] || 'untested';
                                        const bg = s === 'vulnerable' ? 'rgba(239,68,68,0.2)' : s === 'clean' ? 'rgba(34,197,94,0.15)' : 'transparent';
                                        const clr = s === 'vulnerable' ? '#ef4444' : s === 'clean' ? '#22c55e' : 'var(--text-muted)';
                                        return <td key={tt} style={{ padding: '5px 6px', textAlign: 'center', background: bg, color: clr, fontWeight: s !== 'untested' ? 700 : 400, fontSize: 12 }}>
                                          {s === 'vulnerable' ? '!' : s === 'clean' ? '✓' : '·'}
                                        </td>;
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                            {isRunning ? 'Coverage data will appear as tests are executed...' : 'No coverage data yet.'}
                          </div>
                        )}
                      </>
                    )}

                    {/* Chains Tab */}
                    {centerTab === 'chains' && (
                      <>
                        {chains.length > 0 ? chains.map((c, i) => {
                          const key = c.id || String(i);
                          const open = expandedChain === key;
                          const stages = c.stages || [];
                          return (
                          <div key={key} onClick={() => setExpandedChain(open ? null : key)}
                            style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 6, cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{c.name}</div>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                {open ? '\u25be hide' : '\u25b8 show the attack'}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                              {c.outcome ? `An attacker ends up with ${c.outcome}.` : c.impact}
                            </div>
                            {!open && (
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                                {stages.length || (c.findings || []).length} stage{(stages.length || (c.findings || []).length) === 1 ? '' : 's'}
                                {stages.length > 0 && ` \u00b7 ${stages.map(st => st.vuln_type).join(' \u2192 ')}`}
                              </div>
                            )}

                            {open && (
                              <div style={{ marginTop: 10 }}>
                                {stages.length > 0 ? stages.map((st, si) => (
                                  <div key={si} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                    {/* step rail: number plus the line joining it to the next stage */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                      <div style={{ width: 20, height: 20, borderRadius: 10, background: '#ef4444', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {st.step || si + 1}
                                      </div>
                                      {si < stages.length - 1 && (
                                        <div style={{ width: 2, flex: 1, minHeight: 22, background: 'rgba(239,68,68,0.3)', marginTop: 2 }} />
                                      )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                                        {st.title || st.vuln_type}
                                      </div>
                                      {st.gain && (
                                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                                          Gives the attacker {st.gain}.
                                        </div>
                                      )}
                                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, wordBreak: 'break-all' }}>
                                        <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 6 }}>{st.vuln_type}</span>
                                        {st.endpoint}
                                        {st.parameter ? ` [${st.parameter}]` : ''}
                                      </div>
                                      {st.payload && (
                                        <div style={{ fontSize: 10, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: 'var(--text-secondary)', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 6px', marginTop: 4, wordBreak: 'break-all' }}>
                                          {String(st.payload).slice(0, 160)}
                                        </div>
                                      )}
                                      {st.also > 0 && (
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
                                          and {st.also} other finding{st.also === 1 ? '' : 's'} of this type
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )) : (
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.impact}</div>
                                )}

                                {c.rationale && (
                                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(239,68,68,0.2)' }}>
                                    <span style={{ fontWeight: 600 }}>Why the combination matters: </span>
                                    {c.rationale}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        }) : (
                          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                            No attack chains detected yet.
                          </div>
                        )}
                      </>
                    )}

                  </div>
                </>
              );
            })()}
          </Card>

          {/* Right: Controls & Coverage */}
          <Card style={{ padding: 12, width: 260, flexShrink: 0 }}>
            <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Status</h3>

            {/* Budget bars */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>
                <span>Requests</span>
                <span>{activeSession.requests_made || 0} / {activeSession.budget_requests}</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-input)' }}>
                <div style={{ height: '100%', borderRadius: 3, background: 'var(--primary)', width: `${Math.min(((activeSession.requests_made || 0) / activeSession.budget_requests) * 100, 100)}%` }} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>
                <span>Tokens</span><span>{(activeSession.tokens_used || 0).toLocaleString()}</span>
              </div>
            </div>

            {activeSession.coverage_state?.coverage_percent !== undefined && (
              <div style={{ textAlign: 'center', marginBottom: 12, padding: '8px 0', background: 'var(--bg-input)', borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary)' }}>{activeSession.coverage_state.coverage_percent}%</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Coverage</div>
              </div>
            )}

            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span>Endpoints</span><span style={{ fontWeight: 600 }}>{activeSession.world_model?.endpoints?.length || 0}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span>Findings</span><span style={{ fontWeight: 600 }}>{activeSession.findings_count || 0}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span>Chains</span><span style={{ fontWeight: 600 }}>{activeSession.chains_count || 0}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span>Approvals</span><span style={{ fontWeight: 600, color: pendingApprovals.length > 0 ? '#f59e0b' : 'var(--text)' }}>{pendingApprovals.length} pending</span></div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {isRunning && activeSession.status !== 'initializing' && <Button variant="secondary" onClick={() => controlSession(activeSession.id, 'pause')} style={{ width: '100%', fontSize: 11 }}>Pause</Button>}
              {isPaused && !needsAttention && <Button onClick={() => controlSession(activeSession.id, 'resume')} style={{ width: '100%', fontSize: 11 }}>Resume</Button>}
              {isActive && <Button variant="danger" onClick={() => controlSession(activeSession.id, 'stop')} style={{ width: '100%', fontSize: 11 }}>Stop Scan</Button>}
            </div>
          </Card>
        </div>
      )}

      {/* Confirm exactly what will be probed before any request is sent */}
      <Modal isOpen={!!scopeConfirm} onClose={() => setScopeConfirm(null)}
        title="Confirm scope before scanning" width={620}>
        {scopeConfirm?.loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            Loading engagement scope…
          </div>
        ) : scopeConfirm?.data ? (() => {
          const d = scopeConfirm.data;
          const stale = d.drift?.staleInEngagement || [];
          const missing = d.drift?.missingFromEngagement || [];
          const selectedCount = d.scopeItems.filter(s => scopeSelection.includes(s.id)).length;
          const scopeLinkStyle = {
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'var(--primary, #60a5fa)', fontSize: 11, fontFamily: 'inherit',
          };
          return (
            <>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
                <strong style={{ color: 'var(--text)' }}>{d.engagement.name || d.engagement.engagement_type}</strong>
                {d.engagement.client_name ? ` · ${d.engagement.client_name}` : ''}
                {d.engagement.application_name ? ` · ${d.engagement.application_name}` : ''}
              </div>

              {(d.warnings || []).map((w, i) => (
                <div key={i} style={{
                  padding: '10px 14px', borderRadius: 8, marginBottom: 8, fontSize: 12,
                  background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b',
                }}>⚠ {w}</div>
              ))}

              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '14px 0 6px',
              }}>
                <span>Will be tested ({selectedCount} of {d.scopeItems.length})</span>
                {d.scopeItems.length > 1 && (
                  <span style={{ fontWeight: 400, fontSize: 11 }}>
                    <button type="button" onClick={() => setScopeSelection(d.scopeItems.map(x => x.id))}
                      style={scopeLinkStyle}>Select all</button>
                    <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>·</span>
                    <button type="button" onClick={() => setScopeSelection([])}
                      style={scopeLinkStyle}>Clear</button>
                  </span>
                )}
              </div>
              {d.scopeItems.length > 1 && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                  This engagement lists more than one environment. Untick anything the agent must
                  not touch — only ticked targets are authorised, and the agent will refuse to
                  follow a link into any of the others.
                </div>
              )}
              {d.scopeItems.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
                  No scope items — add scope to this engagement before scanning.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
                  {d.scopeItems.map(s => {
                    const isStale = stale.some(x => x.value === s.value);
                    const on = scopeSelection.includes(s.id);
                    return (
                      <label key={s.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 6,
                        cursor: 'pointer', opacity: on ? 1 : 0.55,
                        background: !on ? 'var(--bg)' : (isStale ? 'rgba(239,68,68,0.06)' : 'var(--bg-input)'),
                        border: `1px ${on ? 'solid' : 'dashed'} ${on && isStale ? 'rgba(239,68,68,0.3)' : 'var(--border)'}`,
                      }}>
                        <input type="checkbox" checked={on} onChange={() => setScopeSelection(
                          cur => cur.includes(s.id) ? cur.filter(x => x !== s.id) : [...cur, s.id])} />
                        <Badge>{s.scope_type}</Badge>
                        <code style={{ fontSize: 12, flex: 1, wordBreak: 'break-all' }}>{s.value}</code>
                        {s.description && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {s.description}
                          </span>
                        )}
                        {on && isStale && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', whiteSpace: 'nowrap' }}>
                            NOT ON APPLICATION
                          </span>
                        )}
                        {!on && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            EXCLUDED
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}

              {missing.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '14px 0 6px' }}>
                    On the application but NOT in this engagement — will not be tested ({missing.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
                    {missing.map((s, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 6,
                        background: 'var(--bg)', border: '1px dashed var(--border)', opacity: 0.85,
                      }}>
                        <Badge>{s.scope_type}</Badge>
                        <code style={{ fontSize: 12, flex: 1, wordBreak: 'break-all', color: 'var(--text-secondary)' }}>{s.value}</code>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                    Engagement scope is copied from the application when the engagement is created and is not
                    kept in sync. Update the engagement's Scope tab to test these.
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 18 }}>
                <Button variant="secondary" onClick={() => setScopeConfirm(null)}>Cancel</Button>
                <Button onClick={startScan} disabled={!d.canScan || starting || selectedCount === 0}>
                  {starting ? 'Starting…'
                    : selectedCount === 0 ? 'Select a target to scan'
                    : `Confirm & scan ${selectedCount} target${selectedCount === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          );
        })() : null}
      </Modal>

      {/* Scan Queue — always visible */}
      {sessions.length > 0 && (
        <Card style={{ padding: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Scan Queue</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-input)' }}>
                {['Client', 'Engagement', 'Status', 'Mode', 'Model', 'Destructive', 'Tokens', 'Findings', 'Approvals', 'Started', 'Run time',
                  ...(canDeleteScans ? [''] : [])].map((h, i) => (
                  <th key={h || `actions-${i}`} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const isActive = activeSession?.id === s.id;
                const hasApprovals = (s.pending_approvals || 0) > 0;
                const isLive = isSessionLive(s);
                return (
                  <tr key={s.id} style={{
                    borderTop: '1px solid var(--border)', cursor: 'pointer',
                    background: isActive ? 'rgba(59,130,246,0.06)' : hasApprovals ? 'rgba(245,158,11,0.04)' : 'transparent',
                  }} onClick={() => { setSelectedEng(s.engagement_id); loadSessionDetail(s.id); }}>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{s.client_name || '-'}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, fontSize: 12 }}>{s.engagement_name || s.engagement_type || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: (STATUS_COLORS[s.status] || '#6b7280') + '18', color: STATUS_COLORS[s.status] }}>{s.status}</span>
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11, textTransform: 'capitalize' }}>{s.agent_mode}</td>
                    <td style={{ padding: '8px 12px', fontSize: 11 }}>
                      {/* What it ran on, not what was asked for: the worker
                          falls back to local Ollama when Bedrock cannot be
                          reached, and the row used to keep reporting the
                          requested model — so two scans that claimed different
                          models had in fact used the same one. */}
                      {s.ai_model_used || s.ai_model}
                      {s.ai_model_fallback_reason && (
                        <span title={s.ai_model_fallback_reason}
                          style={{ marginLeft: 5, fontSize: 9, fontWeight: 700, color: '#f59e0b' }}>
                          &#9888;{s.ai_model}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11 }}>
                      {/* The one setting that writes to the target. It was only
                          visible on the launch form, which resets on reload, so
                          a running scan gave no indication either way. */}
                      {s.allow_destructive ? (
                        <span style={{ fontWeight: 700, color: '#f59e0b' }}>Yes</span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>No</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                      {(s.tokens_used || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{s.findings_count || 0}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {hasApprovals ? (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                          🖐 {s.pending_approvals}
                        </span>
                      ) : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-secondary)' }}>
                      {s.created_at ? new Date(s.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td style={{
                      padding: '8px 12px', fontSize: 11, fontVariantNumeric: 'tabular-nums',
                      textAlign: 'right', whiteSpace: 'nowrap',
                      // A live scan's elapsed time is the number an operator
                      // watches, so it gets the emphasis a finished one doesn't.
                      color: isLive ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontWeight: isLive ? 600 : 400,
                    }}>
                      {formatRunTime(runTimeMs(s))}
                    </td>
                    {canDeleteScans && (
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {isLive ? (
                          <span title="Stop the scan before deleting it"
                            style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'not-allowed' }}>—</span>
                        ) : (
                          <button onClick={e => deleteSession(s, e)} disabled={deletingId === s.id}
                            title="Delete this scan record"
                            style={{
                              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                              padding: '2px 8px', fontSize: 11, fontFamily: 'inherit',
                              color: deletingId === s.id ? 'var(--text-muted)' : '#ef4444',
                              cursor: deletingId === s.id ? 'wait' : 'pointer',
                            }}>
                            {deletingId === s.id ? '…' : 'Delete'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {/* Totals. Token spend is the number that decides what a scan
                cost, and it was only ever visible one session at a time. */}
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-input)' }}>
                <td colSpan={5} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {sessions.length} scan{sessions.length === 1 ? '' : 's'}
                  {sessions.some(s => s.allow_destructive) &&
                    ` · ${sessions.filter(s => s.allow_destructive).length} destructive`}
                </td>
                <td style={{ padding: '8px 12px' }} />
                <td style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {sessions.reduce((t, s) => t + (s.tokens_used || 0), 0).toLocaleString()}
                </td>
                <td style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700 }}>
                  {sessions.reduce((t, s) => t + (s.findings_count || 0), 0)}
                </td>
                <td colSpan={2} style={{ padding: '8px 12px' }} />
                <td style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums', textAlign: 'right', whiteSpace: 'nowrap',
                }}>
                  {formatRunTime(sessions.reduce((t, s) => t + (runTimeMs(s) || 0), 0))}
                </td>
                {canDeleteScans && <td style={{ padding: '8px 12px' }} />}
              </tr>
            </tfoot>
          </table>
        </Card>
      )}
    </div>
  );
}
